/**
 * AST → canonical `IF ...` text emitter.
 *
 * Always emits `<<...>>` for label references (per plan, removes ambiguity
 * with reserved words). Adds parentheses when AND/OR children mix precedence.
 */

import {
  ASTNode,
  ComparisonNode,
  GroupNode,
  MembershipNode,
  NotNode,
  Operand,
  SetOperand,
  WhereOperand,
  CountOperand,
  quoteLabel,
} from "./grammar"

/** Emit a complete IF line. */
export function generateIfLine(ast: ASTNode): string {
  return `IF ${emitNode(ast, /*parentOp*/ null)}`
}

/** Emit just the boolean expression (without `IF `). */
export function generateExpression(ast: ASTNode): string {
  return emitNode(ast, null)
}

function emitNode(node: ASTNode, parentOp: "AND" | "OR" | "NOT" | null): string {
  switch (node.kind) {
    case "placeholder":
      return emitPlaceholder()
    case "cmp":
      return emitComparison(node)
    case "set-cmp":
      return emitMembership(node)
    case "not":
      return emitNot(node)
    case "group":
      return emitGroup(node, parentOp)
  }
}

function emitPlaceholder(): string {
  return "…"
}

function emitComparison(node: ComparisonNode): string {
  const left = emitOperand(node.left)
  const right = emitOperand(node.right)
  return `${left} ${node.op} ${right}`
}

function emitMembership(node: MembershipNode): string {
  const left = emitOperand(node.left)
  const right = emitOperand(node.right)
  return `${left} ${node.op} ${right}`
}

function emitNot(node: NotNode): string {
  const inner = emitNode(node.child, "NOT")
  // Always parenthesise NOT's argument for clarity.
  return `NOT (${inner})`
}

function emitGroup(node: GroupNode, parentOp: "AND" | "OR" | "NOT" | null): string {
  if (node.children.length === 0) return emitPlaceholder()
  if (node.children.length === 1) return emitNode(node.children[0], parentOp)

  const joined = node.children
    .map((c) => emitNode(c, node.op))
    .join(` ${node.op} `)

  // Parenthesise when nested inside a different-precedence parent (mix of AND/OR)
  // or when inside NOT.
  const needsParens =
    parentOp === "NOT" ||
    (parentOp !== null && parentOp !== node.op)
  return needsParens ? `(${joined})` : joined
}

// ─── Operands ────────────────────────────────────────────────────────────────

export function emitOperand(operand: Operand): string {
  switch (operand.kind) {
    case "qref":
      return quoteLabel(operand.label)
    case "aref":
      return quoteLabel(operand.label)
    case "num":
      return formatNumber(operand.value)
    case "set":
      return emitSet(operand)
    case "count":
      return emitCount(operand)
    case "where":
      return emitWhere(operand)
  }
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return String(n)
  return String(n)
}

function emitSet(set: SetOperand): string {
  const items = set.items.map(emitOperand).join(", ")
  return `{${items}}`
}

function emitCount(node: CountOperand): string {
  return `COUNT(${emitOperand(node.arg)})`
}

function emitWhere(node: WhereOperand): string {
  const q = emitOperand(node.question)
  const axis = node.axis ? `${node.axis} ` : ""
  const key = emitOperand(node.key)
  return `${q} WHERE ${axis}${key}`
}

// ─── Validation ──────────────────────────────────────────────────────────────

/**
 * Returns true if the AST is fully populated (no placeholders, no missing operands).
 * The wizard's Apply button keys off this.
 */
export function isASTComplete(node: ASTNode): boolean {
  switch (node.kind) {
    case "placeholder":
      return false
    case "cmp":
      return isOperandComplete(node.left) && isOperandComplete(node.right)
    case "set-cmp":
      return (
        isOperandComplete(node.left) &&
        node.right.items.length > 0 &&
        node.right.items.every(isOperandComplete)
      )
    case "not":
      return isASTComplete(node.child)
    case "group":
      return node.children.length > 0 && node.children.every(isASTComplete)
  }
}

function isOperandComplete(o: Operand): boolean {
  switch (o.kind) {
    case "qref":
      return o.label.trim() !== ""
    case "aref":
      return o.label.trim() !== ""
    case "num":
      return Number.isFinite(o.value)
    case "set":
      return o.items.length > 0 && o.items.every(isOperandComplete)
    case "count":
      return isOperandComplete(o.arg)
    case "where":
      return isOperandComplete(o.question) && isOperandComplete(o.key)
  }
}
